import { useMemo, useState } from 'react';
import { Bill } from '@/lib/types';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  isBefore,
} from 'date-fns';

interface Props {
  bills: Bill[];
  onMarkPaid: (id: string) => void;
  onMarkUnpaid: (id: string) => void;
  onDelete: (id: string) => void;
}

export function BillCalendarView({ bills, onMarkPaid, onMarkUnpaid, onDelete }: Props) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad = getDay(monthStart); // 0=Sun

  const billsByDate = useMemo(() => {
    const map = new Map<string, Bill[]>();
    bills.forEach(bill => {
      const key = bill.dueDate;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(bill);
    });
    return map;
  }, [bills]);

  const selectedBills = selectedDate
    ? billsByDate.get(format(selectedDate, 'yyyy-MM-dd')) || []
    : [];

  const now = new Date();

  return (
    <div className="space-y-3">
      {/* Month nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(m => subMonths(m, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold">{format(currentMonth, 'MMMM yyyy')}</span>
        <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(m => addMonths(m, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 text-center text-[10px] font-medium text-muted-foreground">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px">
        {Array.from({ length: startPad }).map((_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {days.map(day => {
          const key = format(day, 'yyyy-MM-dd');
          const dayBills = billsByDate.get(key) || [];
          const hasUnpaid = dayBills.some(b => !b.paid);
          const hasOverdue = dayBills.some(b => !b.paid && isBefore(day, now) && !isSameDay(day, now));
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          return (
            <button
              key={key}
              onClick={() => setSelectedDate(isSelected ? null : day)}
              className={cn(
                'relative flex flex-col items-center rounded-md py-1.5 text-xs transition-colors',
                isToday(day) && 'font-bold',
                isSelected && 'bg-primary text-primary-foreground',
                !isSelected && dayBills.length > 0 && 'hover:bg-muted',
              )}
            >
              <span>{day.getDate()}</span>
              {dayBills.length > 0 && (
                <div className="mt-0.5 flex gap-0.5">
                  {hasOverdue ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
                  ) : hasUnpaid ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-[10px] text-muted-foreground justify-center">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-destructive" />Overdue</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" />Due</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" />Paid</span>
      </div>

      {/* Selected date detail */}
      {selectedDate && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            {format(selectedDate, 'EEEE, d MMMM yyyy')}
            {selectedBills.length === 0 && ' — no bills due'}
          </p>
          {selectedBills.map(bill => {
            const overdue = !bill.paid && isBefore(new Date(bill.dueDate), now) && !isSameDay(new Date(bill.dueDate), now);
            return (
              <Card key={bill.id} className={cn(overdue && 'border-destructive/50')}>
                <CardContent className="flex items-center gap-2 p-3">
                  {overdue ? (
                    <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                  ) : bill.paid ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                  ) : (
                    <div className="h-4 w-4 shrink-0 rounded-full border-2 border-muted-foreground/30" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{bill.name}</p>
                    <p className="text-[10px] text-muted-foreground">{bill.frequency} · {formatAUD(bill.amount)}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {bill.paid ? (
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onMarkUnpaid(bill.id)}>Undo</Button>
                    ) : (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onMarkPaid(bill.id)}>Pay</Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => onDelete(bill.id)}>Delete</Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
